
import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Typography,
  Button as MuiButton,
  Dialog,
} from '@mui/material';
import { Upload } from '@mui/icons-material';
import { isValidNumber } from '../../../utils/helper';
import { Form, Col, Button, Row } from 'react-bootstrap';
import FileInput from '../../Shared/FileUploadPreview/FileInput';
import { ConstantActions, FlowBuilderActions } from '../../../Services/_actions';
import { TemplateActions } from '../../../Services/_actions/Template/template.actions';
import { useRecoilState } from 'recoil';
import { alertAtom, WhatsAppTempFormAtom } from '../../../Services/_states';
import LoadingButton from '../../Shared/Loading/index.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import {
  BLOCKED_EXTENSIONS,
  WHATSAPP_ALLOWED_MIME,
  WHATSAPP_LIMITS,
} from './template.constant.js';
import BulkTemplateUpload from '../TemplateV1/Template/BulkTemplateUpload.tsx';

const SendTemplateForm = ({ phoneNumber = '', handleCloseModal = () => {} }) => {
  const actions = TemplateActions();
  const constantActions = ConstantActions();
  const flowBuilderActions = FlowBuilderActions();
  const [whatsappTempFormData, setWhatsappTempFormData] = useRecoilState(WhatsAppTempFormAtom);
  const [loading, setLoading] = useState(false);
  const [formDataToSend, setFormDataToSent] = useState({});
  const [validationError, setValidationError] = useState({});
  const [bodyParams, setBodyParams] = useState([]);
  const [buttonUrlParams, setButtonUrlParams] = useState([]);
  const [headerFile, setHeaderFile] = useState(null);
  const { showSnackBar } = useSnackBar();
  const [useCreateImage, setUseCreateImage] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  const headerIsDocument = whatsappTempFormData?.components?.find(
    (component) => component?.type === 'HEADER'
  );

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^([1-9]{1}[0-9]{1,12})$/;
    return phoneRegex.test(phone);
  };

  const validateFormData = () => {
    let errors = {};
    if (
      !validatePhoneNumber(formDataToSend?.phone_number) ||
      formDataToSend?.phone_number.length < 10
    ) {
      errors.phone_number = 'Please enter a valid number';
    }
    bodyParams?.forEach((item, index) => {
      if (!item?.text) errors[`bodyParams_${index}`] = `This field is required`;
    });
    if (
      headerIsDocument &&
      headerIsDocument?.format !== 'TEXT' &&
      !headerFile &&
      !useCreateImage
    ) {
      errors[`headerFile`] = `This field is required`;
    }
    setValidationError(errors);
    return Object.keys(errors).length === 0;
  };

  const createButtonParamsObj = () => {
    let updateObj = buttonUrlParams
      ?.map((item, index) => {
        if (item !== null && item !== undefined) {
          return {
            type: 'button',
            index: `${index}`,
            sub_type: 'url',
            parameters: [
              {
                type: 'text',
                text: item,
              },
            ],
          };
        }
      })
      .filter(Boolean);
    return updateObj;
  };

  const handlePhoneNumberChange = (e) => {
    if (isValidNumber(e?.target?.value))
      setFormDataToSent({ ...formDataToSend, phone_number: e?.target?.value });
    if (!e?.target?.value) setFormDataToSent({ ...formDataToSend, phone_number: '' });
  };

  const handleChangeParams = (e, index) => {
    let arr = [...bodyParams];
    let obj = arr[index];
    obj = { ...obj, text: e?.target?.value };
    delete obj['label'];
    arr[index] = obj;
    setBodyParams(arr);
  };

  const handleSetParams = () => {
    let bodyObj = whatsappTempFormData?.components?.find(
      (component) => component?.type === 'BODY'
    );
    if (bodyObj && bodyObj?.example?.body_text && bodyObj?.example?.body_text?.length > 0) {
      let params = bodyObj?.example?.body_text[0];
      let arr = [];
      params?.forEach((item, index) => {
        arr.push({ type: 'text', text: '', label: item });
      });
      setBodyParams(arr);
    }
    let buttonObj = whatsappTempFormData?.components?.find(
      (component) => component?.type === 'BUTTONS'
    );
    if (buttonObj) {
      setButtonUrlParams(buttonObj?.buttons?.map((x) => null));
    }
  };

  const inferDocType = (mime) => {
    if (WHATSAPP_ALLOWED_MIME.image.includes(mime)) return 'image';
    if (WHATSAPP_ALLOWED_MIME.video.includes(mime)) return 'video';
    if (WHATSAPP_ALLOWED_MIME.audio.includes(mime)) return 'audio';
    if (WHATSAPP_ALLOWED_MIME.file.includes(mime)) return 'file';
    return null;
  };

  const bytesToMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);

  const onSelectHeaderFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fileName = file?.name?.toLowerCase();
    const ext = fileName.split('.').pop();

    if (BLOCKED_EXTENSIONS.has(ext)) {
      showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
      return;
    }

    const typeGroup = inferDocType(file.type);
    if (!typeGroup) {
      showSnackBar(`Unsupported file type (${file.type || ext}).`, 'error');
      return;
    }

    const limit = WHATSAPP_LIMITS[typeGroup];
    if (file.size > limit) {
      showSnackBar(
        `File too large for ${typeGroup}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(
          file.size
        )} MB.`,
        'error'
      );
      return;
    }

    setHeaderFile(file);
  };

  const onHeaderFileRemove = () => setHeaderFile(null);

  const handleSendTemplate = async () => {
    setLoading(true);
    if (!validateFormData()) {
      setLoading(false);
      return;
    }

    let data = { ...formDataToSend };
    data['ts_id'] = whatsappTempFormData?.ts_id || '';
    data['name'] = whatsappTempFormData['name'];
    data['code'] = whatsappTempFormData['language'];
    data['components'] = [{ type: 'body', parameters: bodyParams }];

    try {
      if (headerIsDocument) {
        let link = null;
        let objToAdd = { type: 'header' };

        if (useCreateImage) {
          let ImageUrl = whatsappTempFormData?.media?.header;
          if (!ImageUrl) {
            showSnackBar('No image selected for header. Please upload or choose one.', 'error');
            setLoading(false);
            return;
          }

          objToAdd.parameters = [
            {
              type: 'image',
              caption: 'autogenerated_caption.jpeg',
              link: ImageUrl,
            },
          ];
        } else if (headerFile) {
          let headerFileFormData = new FormData();
          headerFileFormData.append('file', headerFile);

          let fileResponse = await actions.uploadHeaderFile(headerFileFormData, {
            ...whatsappTempFormData,
            botNumber: whatsappTempFormData?.bot_number ?? '',
          });

          if (fileResponse?.status === 200) {
            link = fileResponse?.data?.data?.s3_object?.url ?? '';
          } else {
            showSnackBar('Error when uploading the header file', 'error');
            setLoading(false);
            return 0;
          }

          objToAdd.parameters = [
            {
              type: headerIsDocument?.format?.toLowerCase(),
              caption: fileResponse?.data?.data?.s3_object?.filename ?? '',
              link,
            },
          ];
        } else {
          objToAdd.parameters = [{ type: 'text', text: 'header text' }];
        }

        data['components'].push(objToAdd);
      }

      let res = await actions.sendWhatsappTemplate(data, whatsappTempFormData);

      if (res?.status === 200) {
        showSnackBar('Request Processed', 'success');
        handleSetParams();
        setFormDataToSent({ ...formDataToSend, phone_number: '' });
        setHeaderFile(null);
        handleCloseModal();
      } else {
        handleErrorResponse(res);
      }
    } catch (error) {
      showSnackBar('Failed to send template due to a system error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleErrorResponse = (response) => {
    const errorData = response?.data?.data;

    if (errorData?.general_error) {
      const error = errorData.general_error;

      if (typeof error === 'string') {
        showSnackBar(error, 'error');
        return;
      } else if (error?.error?.message) {
        showSnackBar(error.error.message, 'error');
        return;
      }
    }

    showSnackBar('Failed to Send Template', 'error');
  };

  const handleInputChange = (index, value) => {
    let arr = [...buttonUrlParams];
    arr[index] = value;
    setButtonUrlParams(arr);
  };

  useEffect(() => {
    handleSetParams();
  }, [whatsappTempFormData]);

  useEffect(() => {
    if (phoneNumber) handlePhoneNumberChange({ target: { value: phoneNumber } });
  }, []);

  return (
    <Box>
      {/* Bulk Upload Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <MuiButton
          variant="outlined"
          startIcon={<Upload />}
          onClick={() => setShowBulkUpload(true)}
          size="small"
        >
          Upload Sheet for Bulk Send
        </MuiButton>
      </Box>

      <TextField
        label={
          <Typography component="span">
            Phone Number <span style={{ color: 'red' }}>*</span>
          </Typography>
        }
        fullWidth
        value={formDataToSend?.phone_number ?? ''}
        onChange={handlePhoneNumberChange}
        sx={{ marginBottom: 2 }}
        margin="dense"
        variant="outlined"
        size="small"
        error={!!validationError?.phone_number}
        helperText={validationError?.phone_number}
        inputProps={{
          maxLength: 13,
          inputMode: 'numeric',
          pattern: '[0-9]*',
        }}
      />

      {bodyParams?.length ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Body Parameters
          </Typography>
          <Grid container spacing={1}>
            {bodyParams?.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <TextField
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  size="small"
                  label={item?.label || 'Body Parameter'}
                  value={item?.text || ''}
                  onChange={(e) => handleChangeParams(e, index)}
                  error={Boolean(validationError?.[`bodyParams_${index}`])}
                  helperText={validationError?.[`bodyParams_${index}`] || ''}
                  placeholder={item?.text || `Enter ${item?.label || ''}`}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : null}

      {headerIsDocument && headerIsDocument?.format !== 'TEXT' ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Header {headerIsDocument?.format?.toLowerCase()}
          </Typography>
          <FileInput
            onFileSelect={onSelectHeaderFile}
            onFileRemove={onHeaderFileRemove}
            file={headerFile}
            setFile={setHeaderFile}
            accept={
              headerIsDocument?.format === 'IMAGE'
                ? 'image/jpeg,image/png'
                : headerIsDocument?.format === 'VIDEO'
                ? 'video/mp4,video/3gpp'
                : '.pdf,.doc,.docx,.xls,.xlsx,.txt'
            }
          />
          <FormHelperText sx={{ mt: 1 }}>
            {headerIsDocument?.format === 'IMAGE' && 'Allowed: JPG, PNG • Max 5 MB'}
            {headerIsDocument?.format === 'VIDEO' && 'Allowed: MP4, 3GP • Max 16 MB'}
            {headerIsDocument?.format === 'DOCUMENT' &&
              'Allowed: PDF, DOC, DOCX, XLS, XLSX, TXT • Max 100 MB'}
          </FormHelperText>
          {headerIsDocument?.format && headerIsDocument?.format !== 'TEXT' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={useCreateImage}
                  onChange={(e) => setUseCreateImage(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" component="span">
                  Do you want to upload the created {headerIsDocument.format.toLowerCase()}?
                </Typography>
              }
            />
          )}
        </Box>
      ) : null}

      {whatsappTempFormData?.components?.map((component, componentIndex) => {
        if (component.type === 'BUTTONS') {
          const buttonsWithExamples = component?.buttons?.filter(
            (btn) => Array.isArray(btn?.example) && btn?.example?.length > 0
          );
          if (buttonsWithExamples.length === 0) return null;
          return (
            <Box key={componentIndex} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Buttons
              </Typography>
              {buttonsWithExamples.map((button, buttonIndex) => (
                <Box key={buttonIndex} sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {buttonIndex + 1}. {button?.text}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label={`Enter ${buttonIndex + 1}`}
                        value={buttonUrlParams[buttonIndex] || ''}
                        onChange={(e) => handleInputChange(buttonIndex, e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          );
        }
        return null;
      })}

      <Box sx={{ float: 'right', mt: 2 }}>
        <LoadingButton
          buttonName="Send"
          onClickButton={handleSendTemplate}
          isDisabled={!formDataToSend?.phone_number}
          isLoading={loading}
        />
      </Box>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkUpload} onClose={() => setShowBulkUpload(false)} maxWidth="lg" fullWidth>
        <BulkTemplateUpload
          templateData={whatsappTempFormData}
          onClose={() => setShowBulkUpload(false)}
        />
      </Dialog>
    </Box>
  );
};

export default SendTemplateForm;



// import React, { useEffect, useState } from 'react';
// import { Box, Checkbox, FormControlLabel, FormHelperText, Grid, TextField, Typography } from '@mui/material';
// import { isValidNumber, isValidPhoneNumber } from '../../../utils/helper';
// import ErrorWrapper from '../../Shared/ErrorWrapper';
// import { Form, Col, Button, Row } from 'react-bootstrap';
// import FileInput from '../../Shared/FileUploadPreview/FileInput';
// import { ConstantActions, FlowBuilderActions } from '../../../Services/_actions';
// import { TemplateActions } from '../../../Services/_actions/Template/template.actions';
// import { useRecoilState } from 'recoil';
// import { alertAtom, WhatsAppTempFormAtom } from '../../../Services/_states';
// import LoadingButton from '../../Shared/Loading/index.tsx';
// import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
// import { BLOCKED_EXTENSIONS, WHATSAPP_ALLOWED_MIME, WHATSAPP_LIMITS } from './template.constant.js';

// const SendTemplateForm = ({ phoneNumber = '', handleCloseModal = () => { } }) => {
//   const actions = TemplateActions();
//   const constantActions = ConstantActions();
//   const flowBuilderActions = FlowBuilderActions();
//   const [whatsappTempFormData, setWhatsappTempFormData] = useRecoilState(WhatsAppTempFormAtom);
//   const [loading, setLoading] = useState(false);
//   const [formDataToSend, setFormDataToSent] = useState({});
//   const [validationError, setValidationError] = useState({});
//   const [bodyParams, setBodyParams] = useState([]);
//   const [buttonUrlParams, setButtonUrlParams] = useState([]);
//   const [headerFile, setHeaderFile] = useState(null);
//   const { showSnackBar } = useSnackBar();
//   const [useCreateImage, setUseCreateImage] = useState(false);
//   const headerIsDocument = whatsappTempFormData?.components?.find(
//     (component) => component?.type === 'HEADER'
//   );
//   const validatePhoneNumber = (phone) => {
//     const phoneRegex = /^([1-9]{1}[0-9]{1,12})$/;
//     return phoneRegex.test(phone);
//   };

//   const validateFormData = () => {
//     let errors = {};
//     if (
//       !validatePhoneNumber(formDataToSend?.phone_number) ||
//       formDataToSend?.phone_number.length < 10
//     ) {
//       errors.phone_number = 'Please enter a valid number';
//     }
//     bodyParams?.forEach((item, index) => {
//       if (!item?.text) errors[`bodyParams_${index}`] = `This field is required`;
//     });
//     if (headerIsDocument && headerIsDocument?.format !== 'TEXT' && !headerFile && !useCreateImage) {
//       errors[`headerFile`] = `This field is required`;
//     }
//     setValidationError(errors);
//     return Object.keys(errors).length === 0;
//   };

//   const createButtonParamsObj = () => {
//     let updateObj = buttonUrlParams
//       ?.map((item, index) => {
//         if (item !== null && item !== undefined) {
//           // Check for non-null and non-undefined
//           return {
//             type: 'button',
//             index: `${index}`,
//             sub_type: 'url',
//             parameters: [
//               {
//                 type: 'text',
//                 text: item,
//               },
//             ],
//           };
//         }
//       })
//       .filter(Boolean);
//     return updateObj;
//   };

//   const handlePhoneNumberChange = (e) => {
//     if (isValidNumber(e?.target?.value))
//       // if (isValidNumber(e?.target?.value) )
//       setFormDataToSent({ ...formDataToSend, phone_number: e?.target?.value });
//     if (!e?.target?.value) setFormDataToSent({ ...formDataToSend, phone_number: '' });
//   };

//   const handleChangeParams = (e, index) => {
//     let arr = [...bodyParams];
//     let obj = arr[index];
//     obj = { ...obj, text: e?.target?.value };
//     delete obj['label'];
//     arr[index] = obj;
//     setBodyParams(arr);
//   };

//   const handleSetParams = () => {
//     let bodyObj = whatsappTempFormData?.components?.find((component) => component?.type === 'BODY');
//     if (bodyObj && bodyObj?.example?.body_text && bodyObj?.example?.body_text?.length > 0) {
//       let params = bodyObj?.example?.body_text[0];
//       let arr = [];
//       params?.forEach((item, index) => {
//         arr.push({ type: 'text', text: '', label: item });
//       });
//       setBodyParams(arr);
//     }
//     let buttonObj = whatsappTempFormData?.components?.find(
//       (component) => component?.type === 'BUTTONS'
//     );
//     if (buttonObj) {
//       setButtonUrlParams(buttonObj?.buttons?.map((x) => null));
//     }
//   };

//   const inferDocType = (mime: string): 'image' | 'video' | 'audio' | 'file' | null => {
//     if (WHATSAPP_ALLOWED_MIME.image.includes(mime)) return 'image';
//     if (WHATSAPP_ALLOWED_MIME.video.includes(mime)) return 'video';
//     if (WHATSAPP_ALLOWED_MIME.audio.includes(mime)) return 'audio';
//     if (WHATSAPP_ALLOWED_MIME.file.includes(mime)) return 'file';
//     return null;
//   };
//   const bytesToMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);

//   const onSelectHeaderFile = (e) => {
//     // const selectedFile = e?.target?.files[0];
//     // if (!selectedFile) return;

//     // const fileName = selectedFile.name.toLowerCase();
//     // const fileExtension = fileName.split('.').pop();

//     // const imageExtensions = ['jpeg', 'png'];
//     // const videoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'webm'];
//     // const documentExtensions = ['pdf', 'xls', 'xlsx', 'doc', 'docx', 'txt'];

//     // if (BLOCKED_EXTENSIONS.has(fileExtension)) {
//     //   showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
//     //   e.currentTarget.value = ''; // reset input
//     //   return;
//     // }

//     // const format = headerIsDocument?.format;

//     // if (format === 'IMAGE' && !imageExtensions.includes(fileExtension)) {
//     //   showSnackBar('Only image files are allowed.', 'error');
//     //   return;
//     // }

//     // if (format === 'VIDEO' && !videoExtensions.includes(fileExtension)) {
//     //   showSnackBar('Only video files are allowed.', 'error');
//     //   return;
//     // }

//     // if (!format && !documentExtensions.includes(fileExtension)) {
//     //   showSnackBar('Only PDF, Excel, and Word files are allowed.', 'error');
//     //   return;
//     // }
//     // setHeaderFile(selectedFile);

//     const file = e.target.files?.[0];
//     if (!file) return;
//     const fileName = file?.name?.toLowerCase();
//     const ext = fileName.split('.').pop();
//     // 1) Block disallowed / risky extensions
//     // const ext = getExt(file.name);
//     if (BLOCKED_EXTENSIONS.has(ext)) {
//       showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
//       // e.currentTarget.value = ''; // reset input
//       return;
//     }

//     // 2) Validate MIME (must match our allowed lists)
//     const typeGroup = inferDocType(file.type);
//     if (!typeGroup) {
//       showSnackBar(`Unsupported file type (${file.type || ext}).`, 'error');
//       // e.currentTarget.value = '';
//       return;
//     }

//     // 3) Enforce WhatsApp limits per media group
//     const limit = WHATSAPP_LIMITS[typeGroup];
//     if (file.size > limit) {
//       showSnackBar(
//         `File too large for ${typeGroup}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(file.size)} MB.`,
//         'error'
//       );
//       // e.currentTarget.value = '';
//       return;
//     }

//     setHeaderFile(file);
//   };

//   const onHeaderFileRemove = () => setHeaderFile(null);

//   const handleSendTemplate = async () => {
//     setLoading(true);
//     if (!validateFormData()) {
//       setLoading(false);
//       return;
//     }

//     let data = { ...formDataToSend };
//     data['ts_id'] = whatsappTempFormData?.ts_id || '';
//     data['name'] = whatsappTempFormData['name'];
//     data['code'] = whatsappTempFormData['language'];
//     data['components'] = [{ type: 'body', parameters: bodyParams }];
//     try {
//       if (headerIsDocument) {
//         let link = null;
//         let objToAdd = { type: 'header' };

//         if (useCreateImage) {
//           let ImageUrl = whatsappTempFormData?.media?.header;
//           if (!ImageUrl) {
//             showSnackBar('No image selected for header. Please upload or choose one.', 'error');
//             setLoading(false);
//             return;
//           }

//           objToAdd.parameters = [
//             {
//               type: 'image',
//               caption: 'autogenerated_caption.jpeg',
//               link: ImageUrl,
//             },
//           ];
//         } else if (headerFile) {
//           // let my_filename = `${Date.now().toString()}${headerFile?.name}`?.replace(/ /g, '_');
//           // let updatedFile = new File([headerFile], my_filename, { type: headerFile?.type });
//           let headerFileFormData = new FormData();
//           headerFileFormData.append('file', headerFile);
//           // headerFileFormData.append('filename', headerFile?.name ?? '');
//           // headerFileFormData.append('bot_number', whatsappTempFormData?.bot_number);
//           let fileResponse = await actions.uploadHeaderFile(headerFileFormData, {
//             ...whatsappTempFormData,
//             botNumber: whatsappTempFormData?.bot_number ?? '',
//           });
//           // let fileResponse = await flowBuilderActions.uploadMediaToWhatsapp(headerFileFormData);
//           if (fileResponse?.status === 200) {
//             link = fileResponse?.data?.data?.s3_object?.url ?? '';
//           } else {
//             showSnackBar('Error when uploading the header file', 'error');
//             setLoading(false);
//             return 0;
//           }
//           // let resp = await constantActions.uploadFileS3Bucket(updatedFile);
//           // if (resp?.$response?.httpResponse?.statusCode)
//           //   link = `${process.env.REACT_APP_IMAGE_URL}/${my_filename}`;

//           objToAdd.parameters = [
//             {
//               type: headerIsDocument?.format?.toLowerCase(),
//               caption: fileResponse?.data?.data?.s3_object?.filename ?? '',
//               link,
//             },
//           ];
//         } else {
//           objToAdd.parameters = [{ type: 'text', text: 'header text' }];
//         }

//         data['components'].push(objToAdd);
//       }
//       let res = await actions.sendWhatsappTemplate(data, whatsappTempFormData);

//       if (res?.status === 200) {
//         showSnackBar('Template Sent Successfully', 'success');
//         handleSetParams();
//         setFormDataToSent({ ...formDataToSend, phone_number: '' });
//         setHeaderFile(null);
//         handleCloseModal();
//       } else {
//         handleErrorResponse(res); // ✅ Calls the fixed error function
//       }
//     } catch (error) {
//       showSnackBar('Failed to send template due to a system error', 'error');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // **Handles API Error Messages**
//   const handleErrorResponse = (response) => {
//     // ✅ Ensure we access the correct error structure
//     const errorData = response?.data?.data;

//     if (errorData?.general_error) {
//       const error = errorData.general_error;

//       if (typeof error === 'string') {
//         showSnackBar(error, 'error'); // ✅ Displays "User Not OptedIn"
//         return;
//       } else if (error?.error?.message) {
//         showSnackBar(error.error.message, 'error'); // ✅ Handles complex error messages
//         return;
//       }
//     }

//     // ✅ Fallback error message if nothing matches
//     showSnackBar('Failed to Send Template', 'error');
//   };
//   const handleInputChange = (index, value) => {
//     let arr = [...buttonUrlParams];
//     arr[index] = value;
//     setButtonUrlParams(arr);
//   };

//   useEffect(() => {
//     handleSetParams();
//   }, [whatsappTempFormData]);

//   useEffect(() => {
//     if (phoneNumber) handlePhoneNumberChange({ target: { value: phoneNumber } });
//   }, []);

//   return (
//     <Box>
//       <TextField
//         label={
//           <Typography component="span">
//             Phone Number <span style={{ color: 'red' }}>*</span>
//           </Typography>
//         }
//         fullWidth
//         value={formDataToSend?.phone_number ?? ''}
//         onChange={handlePhoneNumberChange}
//         sx={{ marginBottom: 2 }}
//         margin="dense"
//         variant="outlined"
//         size="small"
//         error={!!validationError?.phone_number}
//         helperText={validationError?.phone_number}
//         inputProps={{
//           maxLength: 13,
//           inputMode: 'numeric',
//           pattern: '[0-9]*',
//         }}
//       />

//       {bodyParams?.length ? (
//         <Box sx={{ mb: 3 }}>
//           <Typography variant="subtitle1" gutterBottom>
//             Body Parameters
//           </Typography>
//           <Grid container spacing={1}>
//             {bodyParams?.map((item, index) => (
//               <Grid item xs={12} sm={6} key={index}>
//                 <TextField
//                   fullWidth
//                   margin="dense"
//                   variant="outlined"
//                   size="small"
//                   label={item?.label || 'Body Parameter'}
//                   value={item?.text || ''}
//                   onChange={(e) => handleChangeParams(e, index)}
//                   error={Boolean(validationError?.[`bodyParams_${index}`])}
//                   helperText={validationError?.[`bodyParams_${index}`] || ''}
//                   placeholder={item?.text || `Enter ${item?.label || ''}`}
//                 />
//               </Grid>
//             ))}
//           </Grid>
//         </Box>
//       ) : null}
//       {headerIsDocument && headerIsDocument?.format !== 'TEXT' ? (
//         <Box sx={{ mb: 3 }}>
//           <Typography variant="subtitle1" gutterBottom>
//             Header {headerIsDocument?.format?.toLowerCase()}
//           </Typography>
//           <FileInput
//             onFileSelect={onSelectHeaderFile}
//             onFileRemove={onHeaderFileRemove}
//             file={headerFile}
//             setFile={setHeaderFile}
//             accept={
//               headerIsDocument?.format === 'IMAGE'
//                 ? "image/jpeg,image/png"
//                 : headerIsDocument?.format === 'VIDEO'
//                   ? "video/mp4,video/3gpp"
//                   : '.pdf,.doc,.docx,.xls,.xlsx,.txt'
//             }
//           />
//           {/* {validationError?.headerFile && (
//             <Typography color="error" variant="body2" sx={{ mt: 1 }}>
//               {validationError.headerFile}
//             </Typography>
//           )} */}
//           <FormHelperText sx={{ mt: 1 }}>
//             {headerIsDocument?.format === 'IMAGE' && "Allowed: JPG, PNG • Max 5 MB"}
//             {headerIsDocument?.format === 'VIDEO' && "Allowed: MP4, 3GP • Max 16 MB"}
//             {headerIsDocument?.format === 'DOCUMENT' && "Allowed: PDF, DOC, DOCX, XLS, XLSX, TXT • Max 100 MB"}
//           </FormHelperText>
//           {headerIsDocument?.format && headerIsDocument?.format !== 'TEXT' && (
//             <FormControlLabel
//               control={
//                 <Checkbox
//                   checked={useCreateImage}
//                   onChange={(e) => setUseCreateImage(e.target.checked)}
//                 />
//               }
//               label={
//                 <Typography variant="body2" component="span">
//                   Do you want to upload the created {headerIsDocument.format.toLowerCase()}?
//                 </Typography>
//               }
//             />
//           )}
//         </Box>
//       ) : null}

//       {whatsappTempFormData?.components?.map((component, componentIndex) => {
//         if (component.type === 'BUTTONS') {
//           const buttonsWithExamples = component?.buttons?.filter(
//             (btn) => Array.isArray(btn?.example) && btn?.example?.length > 0
//           );
//           if (buttonsWithExamples.length === 0) return null;
//           return (
//             <Box key={componentIndex} sx={{ mb: 3 }}>
//               <Typography variant="subtitle1" gutterBottom>
//                 Buttons
//               </Typography>
//               {buttonsWithExamples.map((button, buttonIndex) => (
//                 <Box key={buttonIndex} sx={{ mb: 2 }}>
//                   <Typography variant="body1" sx={{ mb: 1 }}>
//                     {buttonIndex + 1}. {button?.text}
//                   </Typography>
//                   <Grid container spacing={2}>
//                     <Grid item xs={12} sm={4}>
//                       <TextField
//                         fullWidth
//                         size="small"
//                         variant="outlined"
//                         label={`Enter ${buttonIndex + 1}`}
//                         value={buttonUrlParams[buttonIndex] || ''}
//                         onChange={(e) => handleInputChange(buttonIndex, e.target.value)}
//                       // error={Boolean(validationError?.[`button_${buttonIndex}`])}
//                       // helperText={validationError?.[`button_${buttonIndex}`] || ''}
//                       />
//                     </Grid>
//                   </Grid>
//                 </Box>
//               ))}
//             </Box>
//           );
//         }
//         return null;
//       })}
//       <Box sx={{ float: 'right', mt: 2 }}>
//         <LoadingButton
//           buttonName="Send"
//           onClickButton={handleSendTemplate}
//           isDisabled={!formDataToSend?.phone_number}
//           isLoading={loading}
//         />
//       </Box>
//     </Box>
//   );
// };

// export default SendTemplateForm;
